import { useState, useCallback, useMemo } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAnalyses } from '../../hooks/useAnalyses';
import { useDashboards } from '../../hooks/useDashboards';
import { useUserConfig, useToggleFavorite, type FavoriteKind } from '../../hooks/useUserConfig';
import { useAppAuth } from '../../hooks/useAppAuth';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { useDataExplorer } from '../../contexts/DataExplorerContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { BarChart3, LineChart, PieChart, Table as TableIcon, Hash, Loader2, Eye, Plus, LayoutDashboard, Star } from 'lucide-react';
import type { Analysis } from '../../services/analysis.service';
import type { Dashboard } from '../../services/dashboard.service';
import { dashboardService } from '../../services/dashboard.service';
import { useTenantUsers } from '../../hooks/useTenantUsers';
import { UserAvatar } from '../../components/UserAvatar';
import { type AnalyticsViewMode } from '../../components/ViewModeToggle';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { useFeatureFlag } from '../../services/featureFlag.service';
import { FeatureFlag } from '../../services/featureFlag.service';
import { cn } from '@/lib/utils';

// Labels match the visualization type names used in the chart editors
// (ChartEditorSidebar / EditChartModal) so the list and the editor agree.
const CHART_TYPES: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string }> = {
  TABLE: { icon: TableIcon, label: 'Table' },
  BAR: { icon: BarChart3, label: 'Bar Chart' },
  LINE: { icon: LineChart, label: 'Line Chart' },
  PIE: { icon: PieChart, label: 'Pie Chart' },
  SINGLE_VALUE: { icon: Hash, label: 'Single Value' },
};

/** Fallback for a type we don't have a label for: SOME_TYPE -> "Some Type". */
function humanizeChartType(type: string): string {
  return type
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// The list endpoints return everything up to this limit; filtering, counting and
// pagination for the Library rail all happen client-side against the full list.
const FETCH_LIMIT = 500;
const PAGE_SIZE = 50;

type LibraryFilter = 'all' | 'recent' | 'favorites' | 'mine';

/** Minimum shape the Library filters need from a chart or a dashboard. */
type LibraryItem = {
  id: string;
  created_by?: string;
  last_updated?: string;
};

function ChartTypeCell({ type }: { type: string }) {
  const entry = CHART_TYPES[type];
  const IconComponent = entry?.icon || BarChart3;
  return (
    <div className="flex items-center gap-2">
      <IconComponent className="h-4 w-4 text-muted-foreground" />
      <span>{entry?.label || humanizeChartType(type)}</span>
    </div>
  );
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return 'Invalid date';
  }
}

function updatedAt(item: LibraryItem): number {
  if (!item.last_updated) return 0;
  const parsed = new Date(item.last_updated).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

function applyLibraryFilter<T extends LibraryItem>(
  items: T[],
  filter: LibraryFilter,
  favoriteIds: Set<string>,
  currentUserId: string | undefined,
): T[] {
  switch (filter) {
    case 'recent':
      return [...items].sort((a, b) => updatedAt(b) - updatedAt(a));
    case 'favorites':
      return items.filter((item) => favoriteIds.has(item.id));
    case 'mine':
      return items.filter((item) => !!currentUserId && item.created_by === currentUserId);
    default:
      return items;
  }
}

interface FavoriteStarProps {
  active: boolean;
  onToggle: () => void;
}

function FavoriteStar({ active, onToggle }: FavoriteStarProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={active ? 'Remove from favorites' : 'Add to favorites'}
      aria-label={active ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={active}
      className={cn(
        'p-1 rounded hover:bg-muted transition-colors',
        active ? 'text-secondary-border' : 'text-muted-foreground/40 hover:text-muted-foreground'
      )}
    >
      <Star className={cn('h-4 w-4', active && 'fill-current')} />
    </button>
  );
}

interface AnalyticsTabsProps {
  value: AnalyticsViewMode;
  onChange: (mode: AnalyticsViewMode) => void;
}

/**
 * Reports / Dashboards as plain text tabs, the active one marked by an orange
 * underline rather than a filled button. ('charts' stays the internal id for
 * the reports tab — it matches the analysis routes and the shared view-mode type.)
 */
function AnalyticsTabs({ value, onChange }: AnalyticsTabsProps) {
  const tabs: { value: AnalyticsViewMode; label: string }[] = [
    { value: 'charts', label: 'Reports' },
    { value: 'dashboards', label: 'Dashboards' },
  ];
  return (
    <div className="flex items-center gap-5 -mx-4 px-4 border-b border-border">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={cn(
            'pb-2 -mb-px text-sm border-b-2 border-transparent text-muted-foreground hover:text-foreground transition-colors',
            value === tab.value && 'border-secondary-border text-foreground font-medium'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface LibraryNavProps {
  value: LibraryFilter;
  onChange: (filter: LibraryFilter) => void;
  options: { value: LibraryFilter; label: string; count?: number }[];
}

function LibraryNav({ value, onChange, options }: LibraryNavProps) {
  return (
    <div>
      <h2 className="px-3 mb-2 text-sm font-semibold text-foreground">Library</h2>
      <nav className="flex flex-col">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            // Selected row: orange bar down the left edge over a grey surface.
            className={cn(
              'flex items-center justify-between gap-2 w-full px-3 py-2 rounded-r-md text-sm text-left border-l-2 border-transparent text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
              value === option.value && 'border-secondary-border bg-muted text-foreground font-medium'
            )}
          >
            <span className="truncate">{option.label}</span>
            {option.count !== undefined && (
              <span className="text-xs tabular-nums opacity-70">{option.count}</span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}

export default function Analytics() {
  const isAnalysisSectionEnabled = useFeatureFlag(FeatureFlag.ACCESS_ANALYSIS_SECTION, false);
  if (!isAnalysisSectionEnabled) {
    return <Navigate to="/" replace />;
  }

  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<AnalyticsViewMode>('charts');
  const [libraryFilter, setLibraryFilter] = useState<LibraryFilter>('all');
  const [chartsPage, setChartsPage] = useState(1);
  const [dashboardsPage, setDashboardsPage] = useState(1);
  const [isCreateDashboardDialogOpen, setIsCreateDashboardDialogOpen] = useState(false);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);
  const { openDataExplorer } = useDataExplorer();
  const isInDraftMode = useIsInDraftMode();

  // Charts data
  const { data: chartsData, isLoading: isChartsLoading, error: chartsError } = useAnalyses({
    page: 1,
    pageSize: FETCH_LIMIT,
    filters: { ephemeral: { $ne: true } },
  });

  // Dashboards data
  const { data: dashboardsData, isLoading: isDashboardsLoading, error: dashboardsError } = useDashboards({
    page: 1,
    pageSize: FETCH_LIMIT,
  });

  // Tenant users for Creator column
  const { data: tenantUsers } = useTenantUsers();

  // Favorites + current user drive the Library filters
  const { data: userConfig } = useUserConfig();
  const { user } = useAppAuth();
  const currentUserId = user?.id;
  const toggleFavorite = useToggleFavorite();

  const handleToggleFavorite = (kind: FavoriteKind, id: string) => {
    toggleFavorite.mutate({ kind, id });
  };

  // Helper to find user by ID
  const getUserById = (userId: string | undefined) => {
    if (!userId || !tenantUsers) return null;
    return tenantUsers.find((u) => u.id === userId) || null;
  };

  const allCharts: Analysis[] = chartsData?.records || [];
  const allDashboards: Dashboard[] = dashboardsData?.records || [];

  const favoriteChartIds = useMemo(
    () => new Set(userConfig?.favorite_analysis_ids || []),
    [userConfig?.favorite_analysis_ids]
  );
  const favoriteDashboardIds = useMemo(
    () => new Set(userConfig?.favorite_dashboard_ids || []),
    [userConfig?.favorite_dashboard_ids]
  );

  const isCharts = viewMode === 'charts';
  const favoriteIds = isCharts ? favoriteChartIds : favoriteDashboardIds;
  const allItems: LibraryItem[] = isCharts ? allCharts : allDashboards;

  const filteredCharts = useMemo(
    () => applyLibraryFilter(allCharts, libraryFilter, favoriteChartIds, currentUserId),
    [allCharts, libraryFilter, favoriteChartIds, currentUserId]
  );
  const filteredDashboards = useMemo(
    () => applyLibraryFilter(allDashboards, libraryFilter, favoriteDashboardIds, currentUserId),
    [allDashboards, libraryFilter, favoriteDashboardIds, currentUserId]
  );

  const libraryOptions = useMemo(() => {
    const noun = isCharts ? 'reports' : 'dashboards';
    return [
      { value: 'all' as const, label: `All ${noun}`, count: allItems.length },
      { value: 'recent' as const, label: 'Recently updated' },
      {
        value: 'favorites' as const,
        label: 'Favorites',
        count: allItems.filter((item) => favoriteIds.has(item.id)).length,
      },
      {
        value: 'mine' as const,
        label: 'Created by me',
        count: allItems.filter((item) => !!currentUserId && item.created_by === currentUserId).length,
      },
    ];
  }, [isCharts, allItems, favoriteIds, currentUserId]);

  const activeLibraryLabel =
    libraryOptions.find((option) => option.value === libraryFilter)?.label ?? '';

  const handleViewModeChange = (mode: AnalyticsViewMode) => {
    setViewMode(mode);
    setChartsPage(1);
    setDashboardsPage(1);
  };

  const handleLibraryFilterChange = (filter: LibraryFilter) => {
    setLibraryFilter(filter);
    setChartsPage(1);
    setDashboardsPage(1);
  };

  const handleViewChart = (chartId: string) => {
    navigate(`/analytics/charts/${chartId}`);
  };

  const handleViewDashboard = (dashboardId: string) => {
    navigate(`/analytics/dashboards/${dashboardId}`);
  };

  const handleNewChart = useCallback(() => {
    // Create a temporary analysis object without saving to database
    // The user can save it via the DataExplorer's save functionality
    const tempAnalysis: Analysis = {
      id: '',
      name: 'Untitled Report',
      visualization_type: 'TABLE',
      dimension_ids: [],
      measure_ids: [],
      dimension_filters: [],
    };

    openDataExplorer({
      analysis: tempAnalysis,
      originalAnalysisId: '',
    });
  }, [openDataExplorer]);

  const handleCreateDashboard = async () => {
    if (!dashboardName.trim()) {
      return;
    }

    setIsCreatingDashboard(true);
    try {
      const newDashboard: Omit<Dashboard, 'id' | 'last_updated'> = {
        name: dashboardName.trim(),
        description: dashboardDescription.trim() || '',
        components: [],
        version_idx: 0,
      };

      const createdDashboard = await dashboardService.create(newDashboard);

      setIsCreateDashboardDialogOpen(false);
      setDashboardName('');
      setDashboardDescription('');

      navigate(`/analytics/dashboards/${createdDashboard.id}`);
    } catch (error) {
      console.error('Failed to create dashboard:', error);
      alert('Failed to create dashboard. Please try again.');
    } finally {
      setIsCreatingDashboard(false);
    }
  };

  const isLoading = isCharts ? isChartsLoading : isDashboardsLoading;
  const error = isCharts ? chartsError : dashboardsError;
  const page = isCharts ? chartsPage : dashboardsPage;
  const setPage = isCharts ? setChartsPage : setDashboardsPage;
  const resultCount = isCharts ? filteredCharts.length : filteredDashboards.length;
  const totalPages = Math.max(1, Math.ceil(resultCount / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const charts = filteredCharts.slice(pageStart, pageStart + PAGE_SIZE);
  const dashboards = filteredDashboards.slice(pageStart, pageStart + PAGE_SIZE);

  const itemNoun = isCharts ? 'reports' : 'dashboards';

  const emptyMessage = (() => {
    if (libraryFilter === 'favorites') {
      return `No favorite ${itemNoun} yet.`;
    }
    if (libraryFilter === 'mine') {
      return `You haven't created any ${itemNoun} yet.`;
    }
    return isCharts
      ? 'No reports found. Create your first report to get started.'
      : 'No dashboards found. Create your first dashboard to get started.';
  })();

  const renderPagination = () =>
    totalPages > 1 && (
      <div className="mt-6">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) setPage(page - 1);
                }}
                style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
              />
            </PaginationItem>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= page - 1 && pageNum <= page + 1)
              ) {
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(pageNum);
                      }}
                      isActive={pageNum === page}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              } else if (pageNum === page - 2 || pageNum === page + 2) {
                return (
                  <PaginationItem key={`ellipsis-${pageNum}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }
              return null;
            })}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) setPage(page + 1);
                }}
                style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );

  return (
    <div className="w-full flex flex-col bg-page-bg overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between w-full px-4 md:px-6 py-3 md:py-4 page-header-surface flex-shrink-0 z-10 gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <SidebarTrigger className="md:hidden h-5 w-5" />
          <div className="flex flex-col gap-1 md:gap-2 min-w-0">
            {/* Page Title */}
            <h1 className="text-lg md:text-xl font-semibold text-foreground">Analytics</h1>
          </div>
        </div>
        <div className="flex items-center gap-3 md:gap-4">
          {/* Create Button */}
          {isCharts ? (
            <Button
              onClick={handleNewChart}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Report
            </Button>
          ) : (
            isInDraftMode && (
              <Button
                onClick={() => setIsCreateDashboardDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                New Dashboard
              </Button>
            )
          )}
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Left rail: view toggle + Library filters */}
        <aside className="w-48 md:w-60 flex-shrink-0 flex flex-col gap-6 p-4 border-r border-border bg-background overflow-y-auto">
          <AnalyticsTabs value={viewMode} onChange={handleViewModeChange} />
          <LibraryNav
            value={libraryFilter}
            onChange={handleLibraryFilterChange}
            options={libraryOptions}
          />
        </aside>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  Loading {itemNoun}...
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-destructive text-sm">
                Failed to load {itemNoun}. Please try again.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-3 mb-6">
                <h2 className="text-xl font-semibold text-foreground">{activeLibraryLabel}</h2>
                <span className="text-sm text-muted-foreground">
                  {resultCount} result{resultCount === 1 ? '' : 's'}
                </span>
              </div>

              {resultCount === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-muted-foreground">{emptyMessage}</p>
                </div>
              ) : isCharts ? (
                // Charts Table
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10" />
                          <TableHead>Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Creator</TableHead>
                          <TableHead>Last Updated</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {charts.map((chart: Analysis) => (
                          <TableRow key={chart.id}>
                            <TableCell className="w-10">
                              <FavoriteStar
                                active={favoriteChartIds.has(chart.id)}
                                onToggle={() => handleToggleFavorite('analysis', chart.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              <Link to={`/analytics/charts/${chart.id}`} className="hover:underline">
                                {chart.name || 'Untitled Report'}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <ChartTypeCell type={chart.visualization_type} />
                            </TableCell>
                            <TableCell>
                              <UserAvatar
                                user={getUserById(chart.created_by)}
                                createdBy={chart.created_by}
                              />
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(chart.last_updated)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewChart(chart.id)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {renderPagination()}
                </>
              ) : (
                // Dashboards Table
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10" />
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Creator</TableHead>
                          <TableHead>Components</TableHead>
                          <TableHead>Last Updated</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboards.map((dashboard: Dashboard) => (
                          <TableRow key={dashboard.id}>
                            <TableCell className="w-10">
                              <FavoriteStar
                                active={favoriteDashboardIds.has(dashboard.id)}
                                onToggle={() => handleToggleFavorite('dashboard', dashboard.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              <Link to={`/analytics/dashboards/${dashboard.id}`} className="flex items-center gap-2 hover:underline">
                                <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                                {dashboard.name || 'Untitled Dashboard'}
                              </Link>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {dashboard.description || '-'}
                            </TableCell>
                            <TableCell>
                              <UserAvatar
                                user={getUserById(dashboard.created_by)}
                                createdBy={dashboard.created_by}
                              />
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {dashboard.components?.length || 0} component{(dashboard.components?.length || 0) !== 1 ? 's' : ''}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(dashboard.last_updated)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDashboard(dashboard.id)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {renderPagination()}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Dashboard Dialog */}
      <Dialog open={isCreateDashboardDialogOpen} onOpenChange={setIsCreateDashboardDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Dashboard</DialogTitle>
            <DialogDescription>
              Enter a name and optional description for your new dashboard. You can configure it after creation.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Input
              placeholder="Dashboard name"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dashboardName.trim() && !isCreatingDashboard) {
                  handleCreateDashboard();
                }
              }}
              autoFocus
            />
            <Input
              placeholder="Description (optional)"
              value={dashboardDescription}
              onChange={(e) => setDashboardDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dashboardName.trim() && !isCreatingDashboard) {
                  handleCreateDashboard();
                }
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDashboardDialogOpen(false);
                setDashboardName('');
                setDashboardDescription('');
              }}
              disabled={isCreatingDashboard}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDashboard}
              disabled={!dashboardName.trim() || isCreatingDashboard}
            >
              {isCreatingDashboard ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
